import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';

// MUI Imports
import {
    AppBar, Toolbar, Typography, Box, Button, IconButton, Menu, MenuItem, InputBase, styled, Paper, Divider, Badge, CircularProgress,
    Autocomplete, TextField, InputAdornment, ToggleButtonGroup, ToggleButton,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, FormControl, InputLabel, Select, Grid
} from '@mui/material';

// Icon Imports
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MicIcon from '@mui/icons-material/Mic';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../api/axios';
import backgroundImageFile from '../assets/copy2.jpeg'; // Make sure the path is correct

// Animation Library
import { motion, AnimatePresence } from 'framer-motion';

// Local component import (functionality is preserved)
import FlowingMenu from './FlowingMenu';
import './AskAIButton.css'; // Import the animation styles

// --- STYLED COMPONENTS TO MATCH THE NEW DESIGN (No changes here) ---

// Main navigation links for the desktop header, now including "Your Orders".
const navLinks = [
    { title: 'HOME', path: '/' },
    { title: 'WHY US', path: '/why-us' },
    { title: 'FAQ', path: '/faq' },
    { title: 'ABOUT', path: '/about' },
    { title: 'CONTACT', path: '/care' },
    { title: 'ORDER NOW', path: '/browse' },
];

// Helper to match modes from ProductCard
const modeLabelMap = {
    case: 'Case(s)',
    piece: 'Piece(s)',
    bag: 'Bag(s)',
    tray: 'Tray(s)',
    weight: 'lb',
};

// Demo items for the FlowingMenu are kept as per your request.
const demoItems = [
    //  { link: 'catalog', text: 'Fresh Summer Produce Now Available !', image: 'https://images.herzindagi.info/image/2022/May/summer-fruits-you-must-have.jpg' },
];


// --- START OF EDIT: Path component for animated hamburger icon ---
const Path = (props) => (
    <motion.path
        fill="transparent"
        strokeWidth="2.5"
        stroke="currentColor"
        strokeLinecap="round"
        {...props}
    />
);
// --- END OF EDIT ---


// Accept props to handle state lifted to App.js
function Header({ onAskAiClick, showAiNotification, setShowAiNotification, cartItemCount, products = [], orderItems = {}, searchTerm = '', onSearchChange, onUpdateCart, onGuestAction, isLoggedIn }) {
    const navigate = useNavigate();
    const location = useLocation(); // To highlight the active link
    const editingOrderId = location.state?.editingOrderId; // Get editingOrderId from current location state
    const [userName, setUserName] = useState('');
    const [localIsLoggedIn, setLocalIsLoggedIn] = useState(false);
    const [isNavigatingCatalog, setIsNavigatingCatalog] = useState(false);
    const [timeoutId, setTimeoutId] = useState(null); // State to hold the timeout ID

    // --- Search State (Managed by parent via props) ---
    // inputValue: Immediate UI state for the text field
    // filteringTerm: Debounced state for filtering the products list
    const [inputValue, setInputValue] = useState(searchTerm);
    const [filteringTerm, setFilteringTerm] = useState(searchTerm);

    // Sync local state when prop changes (e.g. cleared externally)
    useEffect(() => {
        setInputValue(searchTerm);
        setFilteringTerm(searchTerm);
    }, [searchTerm]);

    // Debounced search handler
    const debouncedOnSearchChange = useMemo(
        () => debounce((value) => {
            setFilteringTerm(value);
        }, 300),
        []
    );

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedOnSearchChange.cancel();
        };
    }, [debouncedOnSearchChange]);

    const [quickAddState, setQuickAddState] = useState({});
    // Show search bar only on Browse Page or Category Pages
    const showSearchBar = location.pathname === '/browse' || location.pathname.startsWith('/category/');
    const isDropdownOpen = location.hash === '#search-dropdown';
    const showPrice = process.env.REACT_APP_PRICE === 'true';

    // --- START OF EDIT: Menu open state is now derived from the route ---
    const isMenuOpen = location.pathname === '/mobile-nav';
    // --- END OF EDIT ---

    // Extract category name if on a category page
    const categoryMatch = location.pathname.match(/^\/category\/(.+)$/);
    const categoryName = categoryMatch ? decodeURIComponent(categoryMatch[1]) : null;

    useEffect(() => {
        setIsNavigatingCatalog(false);
        // Clear any lingering timeout when navigation completes
        if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
    }, [location.pathname]);

    // Add a second useEffect for the timeout logic
    useEffect(() => {
        // Only run this effect when isNavigatingCatalog is true
        if (isNavigatingCatalog) {
            // Set a timeout to automatically stop the loading state after 5 seconds
            const id = setTimeout(() => {
                setIsNavigatingCatalog(false);
                setTimeoutId(null);
                // Optionally log a timeout message
                console.log("Navigation timeout reached (5 seconds). Stopping 'Please Wait' state.");
            }, 5000); // 5000 milliseconds = 5 seconds

            // Store the ID so it can be cleared later
            setTimeoutId(id);

            // Cleanup function: this runs if the component unmounts or before the effect runs again
            return () => {
                if (id) {
                    clearTimeout(id);
                }
            };
        }
    }, [isNavigatingCatalog]); // Dependency array: run when isNavigatingCatalog changes

    // --- State for the account dropdown menu ---
    const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);
    const isAccountMenuOpen = Boolean(accountMenuAnchorEl);

    // --- State for the mobile profile menu ---
    const [mobileProfileMenuAnchorEl, setMobileProfileMenuAnchorEl] = useState(null);
    const handleMobileProfileMenuOpen = (event) => setMobileProfileMenuAnchorEl(event.currentTarget);
    const handleMobileProfileMenuClose = () => setMobileProfileMenuAnchorEl(null);

    // --- Special Request State (for no search results) ---
    const [isRequestOpen, setIsRequestOpen] = useState(false);
    const [specialItemName, setSpecialItemName] = useState('');
    const [specialItemQty, setSpecialItemQty] = useState(1);
    const [specialItemUnit, setSpecialItemUnit] = useState('piece');
    const [requestLoading, setRequestLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

    const handleSpecialRequestSubmit = async () => {
        if (!specialItemName.trim()) {
            setNotification({ open: true, message: 'Please provide a description for the item.', severity: 'warning' });
            return;
        }
        if (!isLoggedIn) {
            onGuestAction();
            return;
        }

        setRequestLoading(true);

        try {
            const payload = {
                special_items: [{
                    name: specialItemName,
                    quantity: Number(specialItemQty),
                    mode: specialItemUnit
                }]
            };

            await api.post('/cart', payload);

            setNotification({ open: true, message: "Special item added to your cart!", severity: 'success' });
            setIsRequestOpen(false);
            setSpecialItemName('');
            setSpecialItemQty(1);
            setSpecialItemUnit('piece');
            if (onSearchChange) onSearchChange(''); // Clear search term

        } catch (err) {
            console.error("Failed to add special item:", err);
            setNotification({ open: true, message: "Could not add item. Please try again.", severity: 'error' });
        } finally {
            setRequestLoading(false);
        }
    };

    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') return;
        setNotification({ ...notification, open: false });
    };

    const openSpecialRequestWithSearchTerm = () => {
        setSpecialItemName(inputValue);
        setIsRequestOpen(true);
    };

    useEffect(() => {
        const checkLoginStatus = () => {
            const token = localStorage.getItem('accessToken');
            const name = localStorage.getItem('contactPerson');
            setLocalIsLoggedIn(!!token);
            if (token && name) {
                setUserName(name);
            } else {
                setUserName('');
            }
        };

        checkLoginStatus();
        window.addEventListener('storage', checkLoginStatus);

        return () => window.removeEventListener('storage', checkLoginStatus);
    }, []);

    // Determine if current user is the parent account (hide ordering UI)
    const isParentAccount = localIsLoggedIn && localStorage.getItem('isParent') === 'true';

    // --- START OF EDIT: Simplified handler for the menu toggle using navigation ---
    const handleDrawerToggle = () => {
        if (isMenuOpen) {
            navigate(-1); // Go back if menu is open
        } else {
            navigate('/mobile-nav'); // Navigate to menu page if closed
        }
    };
    // --- END OF EDIT ---

    // --- Desktop Handlers (unchanged) ---
    const handleAccountMenuOpen = (event) => setAccountMenuAnchorEl(event.currentTarget);
    const handleAccountMenuClose = () => setAccountMenuAnchorEl(null);
    const handleAccountMenuItemClick = (path) => {
        // Pass editingOrderId if navigating to cart
        const state = path === '/cart' ? { editingOrderId } : undefined;
        navigate(path, { state });
        handleAccountMenuClose();
    };
    const handleAccountLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('profileIncomplete');
        localStorage.removeItem('parentGroup');
        localStorage.removeItem('isParent');
        localStorage.removeItem('switchedByParent');
        localStorage.removeItem('companyName');
        localStorage.removeItem('email');
        setLocalIsLoggedIn(false);
        window.dispatchEvent(new Event('storage'));
        navigate('/');
        handleAccountMenuClose();
    };

    // --- Search Logic ---
    const productsById = useMemo(() => {
        const map = {};
        products.forEach(p => { if (p && p.id) map[p.id] = p; });
        return map;
    }, [products]);

    const searchOptions = useMemo(() => {
        if (!products) return [];
        // Filter based on the debounced filteringTerm, NOT the immediate inputValue
        if (!filteringTerm || filteringTerm.length < 2) return [];
        const lowered = filteringTerm.toLowerCase();
        return products
            .filter(p => p.name.toLowerCase().includes(lowered))
            .slice(0, 20);
    }, [products, filteringTerm]);

    const handleQuickAddStateChange = useCallback((productId, field, value) => {
        setQuickAddState(prev => {
            const product = productsById[productId];
            const availableModes = product ? Object.keys(product.modes || {}) : ['piece'];
            const currentState = prev[productId] || {
                quantity: 1,
                mode: availableModes[0]
            };

            if (field === 'mode' && currentState.mode !== value) {
                return {
                    ...prev,
                    [productId]: {
                        ...currentState,
                        mode: value,
                        quantity: 1
                    }
                };
            }

            if (field === 'quantity') {
                // Validation Logic adapted from ProductCard.js
                const modeStr = currentState.mode || '';
                const modeLower = modeStr.toLowerCase();
                // Determine if we allow decimals
                const isWeightMode = ['weight', 'lb', 'pound'].includes(modeStr.split('(')[0].trim().toLowerCase());

                let isCaseOrBagMode = modeLower.includes('case') || modeLower.includes('bag');
                if (modeLower.includes('case') && modeStr.includes('*')) isCaseOrBagMode = false;
                if (modeLower.includes('piece')) isCaseOrBagMode = false;

                const regex = (isWeightMode || isCaseOrBagMode) ? /^\d*\.?\d*$/ : /^\d*$/;

                // Value coming from TextField is string, value from buttons might be number
                const stringValue = String(value);

                if (stringValue === '' || regex.test(stringValue)) {
                    return {
                        ...prev,
                        [productId]: { ...currentState, quantity: stringValue }
                    };
                }
                // If invalid, return prev state
                return prev;
            }

            return {
                ...prev,
                [productId]: { ...currentState, [field]: value }
            };
        });
    }, [productsById]);

    const handleQuickAddToCart = useCallback((product) => {
        if (!product || !product.id) return;
        const state = quickAddState[product.id] || {};
        const mode = state.mode || Object.keys(product.modes || {})[0];
        if (!mode) {
            console.error("No mode available for quick add.");
            return;
        }
        const quantity = Number(state.quantity || 1);

        if (isLoggedIn) {
            if (quantity > 0) onUpdateCart(product, quantity, mode);
        } else {
            onGuestAction();
        }
    }, [quickAddState, onUpdateCart, isLoggedIn, onGuestAction]);

    const preventListboxClose = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const executeSearch = (value) => {
        if (onSearchChange) onSearchChange(value);

        // If we are on browse page and have the search dropdown open, clear it
        if (location.pathname === '/browse' && location.hash === '#search-dropdown') {
            navigate(
                { pathname: '/browse', search: `search=${encodeURIComponent(value)}`, hash: '' },
                { replace: true }
            );
        }

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    const searchBarContent = (
        <Autocomplete
            freeSolo
            disableCloseOnSelect
            clearOnBlur={false}
            openOnFocus
            blurOnSelect={false}
            open={isDropdownOpen}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    executeSearch(inputValue);
                }
            }}
            onOpen={() => {
                if (!isDropdownOpen) {
                    navigate({ pathname: location.pathname, search: location.search, hash: 'search-dropdown' }, { replace: false });
                }
            }}
            onClose={(event, reason) => {
                if (window.location.hash === '#search-dropdown') {
                    // Navigate back to the current path without the #search-dropdown hash.
                    // Instead of using the stale React location object, we use window.location
                    // to ensure we don't accidentally override the search query if executeSearch was just called.
                    navigate(
                        {
                            pathname: window.location.pathname,
                            search: window.location.search,
                            hash: ''
                        },
                        { replace: true }
                    );
                }
            }}
            options={searchOptions}
            getOptionLabel={(option) => option.name || ""}
            onInputChange={(event, newInputValue, reason) => {
                // Update local state immediately
                setInputValue(newInputValue);
                // Debounce the update to parent and local filtering
                debouncedOnSearchChange(newInputValue);
                if (reason === 'clear') {
                    if (onSearchChange) onSearchChange('');
                }
            }}
            inputValue={inputValue}
            noOptionsText={
                inputValue && inputValue.length >= 2 ? (
                    <Box
                        sx={{
                            p: 2,
                            textAlign: 'center',
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                background: 'linear-gradient(135deg, #f9fbf7 0%, #edf5e6 100%)',
                                borderRadius: '16px',
                                border: '2px dashed #75a84b',
                            }}
                        >
                            <AutoAwesomeIcon sx={{ fontSize: 40, color: '#75a84b', mb: 1 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e3b1f', mb: 0.5 }}>
                                Can't find "{inputValue}"?
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                                Add it as a special item request!
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={openSpecialRequestWithSearchTerm}
                                sx={{
                                    bgcolor: '#75a84b',
                                    color: 'white',
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    px: 3,
                                    py: 1,
                                    boxShadow: '0 4px 12px rgba(117, 168, 75, 0.3)',
                                    '&:hover': { bgcolor: '#5c8d36' }
                                }}
                            >
                                Request Special Item
                            </Button>
                        </Paper>
                    </Box>
                ) : null
            }
            sx={{
                width: '100%',
                maxWidth: 600,
                '& .MuiAutocomplete-inputRoot': {
                    padding: '6px 16px !important', // refined padding
                    borderRadius: '50px !important',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        borderColor: '#d1d5db',
                    },
                    '&.Mui-focused': {
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 15px rgba(117, 168, 75, 0.2)',
                        borderColor: '#75a84b',
                    }
                }
            }}
            onChange={(event, value, reason) => {
                if (reason === 'selectOption' && value && value.name) {
                    setInputValue(value.name);
                    debouncedOnSearchChange(value.name);
                    executeSearch(value.name);
                }
            }}
            ListboxProps={{
                onMouseDown: (e) => { e.preventDefault(); }, // Prevent blur
                sx: {
                    padding: '8px',
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    mt: 1,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#e0e0e0', borderRadius: '10px' }
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    placeholder="Search products..."
                    InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        startAdornment: null,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        executeSearch(inputValue);
                                    }}
                                    sx={{
                                        bgcolor: '#75a84b', 
                                        color: 'white', 
                                        borderRadius: '50%',
                                        p: 1, 
                                        mr: 1,
                                        '&:hover': { bgcolor: '#5c8d36' },
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: '20px' }} />
                                </IconButton>
                                <IconButton
                                    onClick={() => onAskAiClick('', 'camera')}
                                    sx={{ color: '#75a84b', p: 0.5, mr: 0.5 }}
                                    title="Search with Camera"
                                >
                                    <CameraAltIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    onClick={() => onAskAiClick('', 'mic')}
                                    sx={{ color: '#75a84b', p: 0.5 }}
                                    title="Search with Voice"
                                >
                                    <MicIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: '100%',
                        input: {
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: '#374151',
                            '&::placeholder': {
                                color: '#9ca3af',
                                opacity: 1,
                            },
                        }
                    }}
                />
            )}
            renderOption={(props, option) => {
                const availableModes = Object.keys(option.modes || {});
                if (availableModes.length === 0) return null;
                const itemState = quickAddState[option.id] || { quantity: 1, mode: availableModes[0] };
                const price = option.modes[itemState.mode]?.price?.toFixed?.(2);

                // Check if item is in cart (any mode)
                const isInCart = Object.values(orderItems).some(item => item.product.id === option.id);

                return (
                    <li
                        {...props}
                        key={option.id}
                        style={{ display: 'block', padding: 0, marginBottom: '8px' }}
                    >
                        <Paper
                            elevation={0}
                            onMouseDown={preventListboxClose}
                            onClick={(e) => { e.stopPropagation(); }}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                width: '100%',
                                borderRadius: '12px',
                                border: isInCart ? '2px solid #75a84b' : '1px solid transparent', // Highlight if in cart
                                transition: 'all 0.2s ease',
                                backgroundColor: '#fff',
                                '&:hover': {
                                    backgroundColor: '#f9fcf6',
                                    borderColor: '#e0e7d1',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                }
                            }}
                        >
                            {/* Product Image */}
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    component="img"
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        border: '1px solid #f0f0f0',
                                        p: 0.5,
                                        flexShrink: 0
                                    }}
                                    src={option.image_url || 'https://via.placeholder.com/60x60.png?text=No+Img'}
                                    alt={option.name}
                                />
                                {isInCart && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -6,
                                            right: -6,
                                            backgroundColor: '#75a84b',
                                            borderRadius: '50%',
                                            width: 20,
                                            height: 20,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                {/* Product Name & Price */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937', lineHeight: 1.2 }}>
                                            {option.name}
                                            {isInCart && <Typography component="span" variant="caption" sx={{ color: '#75a84b', ml: 1, fontWeight: 'bold', bgcolor: '#f0f5e4', px: 0.8, py: 0.2, borderRadius: '4px' }}>IN CART</Typography>}
                                        </Typography>
                                        {showPrice && (
                                            <Typography variant="body2" sx={{ color: '#75a84b', fontWeight: 600, mt: 0.5 }}>
                                                {price ? `$${price}` : ''}
                                                <Typography component="span" variant="caption" sx={{ color: '#6b7280', fontWeight: 400, ml: 0.5 }}>
                                                    / {itemState.mode}
                                                </Typography>
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Controls */}
                                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                                    {/* Mode Selector */}
                                    <ToggleButtonGroup
                                        value={itemState.mode}
                                        exclusive
                                        size="small"
                                        onChange={(e, newMode) => { if (newMode) handleQuickAddStateChange(option.id, 'mode', newMode); }}
                                        sx={{
                                            backgroundColor: '#f3f4f6',
                                            borderRadius: '8px',
                                            '& .MuiToggleButton-root': {
                                                border: 'none',
                                                padding: '4px 10px',
                                                fontSize: '0.75rem',
                                                borderRadius: '6px !important',
                                                color: '#6b7280',
                                                margin: '2px',
                                                textTransform: 'capitalize',
                                                fontWeight: 500,
                                                '&.Mui-selected': {
                                                    backgroundColor: '#fff',
                                                    color: '#75a84b',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                    fontWeight: 700
                                                },
                                                '&:hover': {
                                                    backgroundColor: '#e5e7eb'
                                                }
                                            }
                                        }}
                                    >
                                        {availableModes.map(mode => (
                                            <ToggleButton key={mode} value={mode} onMouseDown={preventListboxClose}>
                                                {mode}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>

                                    {/* Quantity & Add */}
                                    <Box display="flex" alignItems="center" sx={{ backgroundColor: '#f3f4f6', borderRadius: '50px', p: '2px' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleQuickAddStateChange(option.id, "quantity", Math.max(1, (Number(itemState.quantity) || 1) - 1))}
                                            sx={{ width: 28, height: 28, color: '#75a84b', '&:hover': { bgcolor: 'white' } }}
                                            onMouseDown={preventListboxClose}
                                        >
                                            -
                                        </IconButton>
                                        <TextField
                                            variant="standard"
                                            value={itemState.quantity}
                                            onChange={(e) => handleQuickAddStateChange(option.id, "quantity", e.target.value)}
                                            sx={{ width: 40 }}
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: { fontSize: '0.9rem', fontWeight: 600, color: '#374151', textAlign: 'center' }
                                            }}
                                            inputProps={{ style: { textAlign: 'center', padding: 0 } }}
                                            onMouseDown={preventListboxClose}
                                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleQuickAddStateChange(option.id, "quantity", (Number(itemState.quantity) || 0) + 1)}
                                            sx={{ width: 28, height: 28, color: '#75a84b', '&:hover': { bgcolor: 'white' } }}
                                            onMouseDown={preventListboxClose}
                                        >
                                            +
                                        </IconButton>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        size="small"
                                        disableElevation
                                        onClick={() => handleQuickAddToCart(option)}
                                        sx={{
                                            backgroundColor: '#75a84b',
                                            color: 'white',
                                            borderRadius: '50px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            minWidth: '70px',
                                            '&:hover': { backgroundColor: '#5c8d36' }
                                        }}
                                        onMouseDown={preventListboxClose}
                                    >
                                        Add
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </li>
                );
            }}
        />
    );

    return (
        <Box>
            <AppBar position="sticky" color="transparent" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', top: 0, zIndex: 1100 }}>
                <Toolbar sx={{ justifyContent: 'space-between', p: { xs: 1, md: '0 24px' }, minHeight: '90px' }}>

                    {/* --- Mobile View --- */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>

                        {/* LEFT SIDE: Menu + Logo OR Back + Category */}
                        {categoryName ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                    onClick={() => navigate('/browse')}
                                    sx={{
                                        color: 'black',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        '&:hover': { backgroundColor: '#e0e0e0' }
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e3b1f', ml: 0.5, lineHeight: 1.2 }}>
                                    {categoryName}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* Animated Menu/Cross Button */}
                                <IconButton
                                    size="large"
                                    onClick={handleDrawerToggle}
                                    sx={{
                                        color: isMenuOpen ? 'black' : 'white',
                                        backgroundColor: isMenuOpen ? 'transparent' : '#75a84b',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        zIndex: 1400, // Ensure button is above the menu
                                        position: 'relative', // Needed for zIndex
                                        transition: 'background-color 0.3s ease, color 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: isMenuOpen ? 'rgba(0,0,0,0.08)' : '#7a8a55'
                                        }
                                    }}
                                >
                                    <motion.svg width="23" height="23" viewBox="0 0 23 23"
                                        animate={isMenuOpen ? "open" : "closed"}
                                    >
                                        <Path
                                            variants={{
                                                closed: { d: "M 2 2.5 L 20 2.5" },
                                                open: { d: "M 3 16.5 L 17 2.5" }
                                            }}
                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                        />
                                        <Path
                                            d="M 2 9.423 L 20 9.423"
                                            variants={{
                                                closed: { opacity: 1 },
                                                open: { opacity: 0 }
                                            }}
                                            transition={{ duration: 0.1 }}
                                        />
                                        <Path
                                            variants={{
                                                closed: { d: "M 2 16.346 L 20 16.346" },
                                                open: { d: "M 3 2.5 L 17 16.346" }
                                            }}
                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                        />
                                    </motion.svg>
                                </IconButton>

                                <Box component={RouterLink} to="/" state={{ intentional: true }} sx={{ display: 'flex', alignItems: 'center', gap: 2, textDecoration: 'none' }}>
                                    <Box
                                        component="img"
                                        src="/logooo2.png"
                                        alt="Matax Express Logo"
                                        sx={{ height: '35px' }}
                                    />
                                    <Box sx={{ ml: -1.5 }}>
                                        <Typography variant="body1" sx={{ color: 'black', fontWeight: 'bold', lineHeight: 1, fontSize: '1.1rem', fontStyle: 'italic' }}>
                                            Matax
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'black', fontWeight: 'bold', lineHeight: 1, fontSize: '1.0rem', fontStyle: 'italic' }}>
                                            Express
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* RIGHT SIDE: Ask AI + Account + Cart */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: '18px' }} />}
                                // --- START OF EDIT: Navigate to the AI Popup Page on mobile ---
                                onClick={() => navigate('/mobile-ai-popup')}
                                // --- END OF EDIT ---
                                sx={{
                                    bgcolor: '#f0f5e4',
                                    color: '#333',
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    padding: '4px 10px',
                                    boxShadow: 'none',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        bgcolor: '#e0e5d4',
                                        boxShadow: 'none',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '4px'
                                    }
                                }}
                            >
                                AI Order
                            </Button>



                            <IconButton
                                onClick={localIsLoggedIn ? handleMobileProfileMenuOpen : undefined}
                                component={localIsLoggedIn ? undefined : RouterLink}
                                to={localIsLoggedIn ? undefined : "/login"}
                                sx={{ color: 'black', padding: '8px' }}
                            >
                                <PersonOutlineIcon />
                            </IconButton>

                            <Menu
                                anchorEl={mobileProfileMenuAnchorEl}
                                open={Boolean(mobileProfileMenuAnchorEl)}
                                onClose={handleMobileProfileMenuClose}
                                PaperProps={{
                                    sx: {
                                        mt: 1,
                                        borderRadius: 2,
                                        boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
                                    }
                                }}
                            >
                                {(localStorage.getItem('isParent') === 'true' || localStorage.getItem('switchedByParent') === 'true') && (
                                    <MenuItem onClick={() => { handleMobileProfileMenuClose(); navigate('/parent-dashboard'); }} sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 1.5, px: 3, color: '#75a84b' }}>Parent Dashboard</MenuItem>
                                )}
                                <MenuItem onClick={() => { handleMobileProfileMenuClose(); navigate('/profile'); }} sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 1.5, px: 3 }}>Your Profile</MenuItem>
                                {!isParentAccount && (
                                    <MenuItem onClick={() => { handleMobileProfileMenuClose(); navigate('/order'); }} sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 1.5, px: 3 }}>Your Orders</MenuItem>
                                )}
                                <MenuItem onClick={() => { handleMobileProfileMenuClose(); navigate('/statements'); }} sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 1.5, px: 3 }}>Your Statements</MenuItem>
                            </Menu>

                            <IconButton
                                component={RouterLink}
                                to="/cart"
                                state={{ editingOrderId }} // Pass editingOrderId state
                                sx={{ color: 'black', padding: '8px' }}
                            >
                                <Badge badgeContent={cartItemCount} color="primary">
                                    <ShoppingCartOutlinedIcon />
                                </Badge>
                            </IconButton>
                        </Box>
                    </Box>

                    {/* --- Desktop View: (Unchanged) --- */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, width: '100%', alignItems: 'center' }}>

                        <Box component={RouterLink} to="/" state={{ intentional: true }} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', flexShrink: 0, pr: 3 }}>
                            <Box component="img" src="/logooo2.png" alt="Matax Express Logo" sx={{ height: '50px', mr: 1.5 }} />
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'black', lineHeight: 1, fontSize: '1.1rem', fontStyle: 'italic' }}>
                                    Matax
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'black', lineHeight: 1, fontStyle: 'italic', fontSize: '1rem' }}>
                                    Express
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: 'white',
                            borderTopLeftRadius: 100,
                            borderBottomLeftRadius: 100,
                            padding: '12px 30px 12px 100px',
                            marginLeft: '40px',
                        }}>
                            {/* Navigation Links - Hide when search is active to keep it clean, OR stack them? 
                                User asked for it to be clean. Let's keep links but maybe shrink them or just place search nicely.
                            */}
                            <Box sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                                {navLinks.filter(link => {
                                    if (showSearchBar && ['WHY US', 'FAQ', 'ABOUT'].includes(link.title)) {
                                        return false;
                                    }
                                    // Hide ORDER NOW for parent accounts
                                    if (isParentAccount && link.title === 'ORDER NOW') {
                                        return false;
                                    }
                                    return true;
                                }).map((link) => (
                                    link.title === 'ORDER NOW' ? (
                                        <Button
                                            key={link.title}
                                            onClick={() => {
                                                if (location.pathname !== link.path) {
                                                    setIsNavigatingCatalog(true);
                                                }
                                                setTimeout(() => navigate(link.path), 10);
                                            }}
                                            disabled={isNavigatingCatalog}
                                            startIcon={isNavigatingCatalog ? <CircularProgress size={16} color="inherit" /> : null}
                                            sx={{ color: '#333', fontWeight: 'bold', mx: 1.5, textTransform: 'uppercase' }}
                                        >
                                            {isNavigatingCatalog ? "Please Wait" : link.title}
                                        </Button>
                                    ) : (
                                        <Button
                                            key={link.title}
                                            component={RouterLink}
                                            to={link.path}
                                            state={link.path === '/' ? { intentional: true } : undefined}
                                            sx={{ color: '#333', fontWeight: 'bold', mx: 1.5, textTransform: 'uppercase' }}
                                        >
                                            {link.title}
                                        </Button>
                                    )
                                ))}
                            </Box>

                            {showSearchBar && (
                                <Box sx={{ flexGrow: 1, mx: 4, display: 'flex', justifyContent: 'center' }}>
                                    {searchBarContent}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: '20px' }} />}
                                    onClick={onAskAiClick}
                                    className="ask-ai-desktop-btn"
                                    sx={{
                                        bgcolor: '#f0f5e4',
                                        color: '#333',
                                        borderRadius: '50px',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                        padding: '8px 16px',
                                        boxShadow: 'none',
                                        whiteSpace: 'nowrap',
                                        // Hover styles are now handled by AskAIButton.css for the animation
                                        '& .MuiButton-startIcon': {
                                            marginRight: '4px'
                                        }
                                    }}
                                >
                                    AI Order
                                </Button>

                                {localIsLoggedIn ? (
                                    <>
                                        <Button
                                            id="account-menu-button"
                                            aria-controls={isAccountMenuOpen ? 'account-menu' : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={isAccountMenuOpen ? 'true' : undefined}
                                            onClick={handleAccountMenuOpen}
                                            endIcon={<ArrowDropDownIcon sx={{ color: '#666' }} />}
                                            sx={{ color: 'black', textTransform: 'none', textAlign: 'left', lineHeight: 1.2, p: '6px 8px' }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontSize: '12px', color: '#555', textTransform: 'capitalize' }}>
                                                    Hello, {userName}
                                                </Typography>
                                                <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>Your Account & Orders</Typography>
                                            </Box>
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        component={RouterLink} to="/login"
                                        sx={{ color: 'black', textTransform: 'none', textAlign: 'left', lineHeight: 1.2, p: '6px 8px' }}
                                    >
                                        <Box>
                                            <Typography sx={{ color: '#333', textTransform: 'none', fontWeight: '600' }}>Log in </Typography>
                                        </Box>
                                    </Button>
                                )}

                                {!isParentAccount && (
                                <Button
                                    component={RouterLink} to="/cart"
                                    state={{ editingOrderId }} // Pass editingOrderId state
                                    startIcon={
                                        <Badge badgeContent={cartItemCount} color="primary">
                                            <ShoppingCartOutlinedIcon />
                                        </Badge>
                                    }
                                    sx={{ color: '#333', textTransform: 'none', fontWeight: '600' }}
                                >
                                    Cart
                                </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Toolbar>
                {/* --- Mobile Search Bar (Below Main Toolbar) --- */}
                {showSearchBar && (
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, p: 2, pt: 0, bgcolor: 'white', borderBottom: '1px solid #f0f0f0', justifyContent: 'center' }}>
                        {searchBarContent}
                    </Box>
                )}
            </AppBar>

            {/* --- Desktop Account Menu (Unchanged) --- */}
            <Menu
                id="account-menu"
                anchorEl={accountMenuAnchorEl}
                open={isAccountMenuOpen}
                onClose={handleAccountMenuClose}
                MenuListProps={{ 'aria-labelledby': 'account-menu-button', sx: { p: 0 } }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
                        mt: 1.5,
                        width: 280,
                        borderRadius: '4px',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 28,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ p: '8px 16px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 1, fontSize: '1.1rem' }}>Your Account</Typography>
                    {(localStorage.getItem('isParent') === 'true' || localStorage.getItem('switchedByParent') === 'true') && (
                        <MenuItem onClick={() => handleAccountMenuItemClick('/parent-dashboard')} sx={{ borderRadius: 1, p: 1.5, color: '#75a84b', fontWeight: 600 }}>Parent Dashboard</MenuItem>
                    )}
                    <MenuItem onClick={() => handleAccountMenuItemClick('/profile')} sx={{ borderRadius: 1, p: 1.5 }}>Your Profile</MenuItem>
                    {!isParentAccount && (
                        <MenuItem onClick={() => handleAccountMenuItemClick('/order')} sx={{ borderRadius: 1, p: 1.5 }}>Your Orders</MenuItem>
                    )}
                    <MenuItem onClick={() => handleAccountMenuItemClick('/statements')} sx={{ borderRadius: 1, p: 1.5 }}>Your Statements</MenuItem>
                    {!isParentAccount && (
                        <MenuItem onClick={() => handleAccountMenuItemClick('/cart')} sx={{ borderRadius: 1, p: 1.5 }}>Your Cart</MenuItem>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem onClick={handleAccountLogout} sx={{ borderRadius: 1, p: 1.5 }}>Log Out</MenuItem>
                </Box>
            </Menu>

            <Box sx={{ borderTop: '1px solid #e0e0e0', width: '100%', bgcolor: '#f8f7f5' }}>
                <FlowingMenu items={demoItems} />
            </Box>

            {/* --- START OF EDIT: Mobile menu and AI popup are now handled by routes, so they are removed from here --- */}

            <AnimatePresence>
                {showAiNotification && (
                    <>
                        {/* --- Desktop AI Popup (Original, but now conditionally hidden on mobile at the App level) --- */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 50 }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                                style={{
                                    position: 'fixed',
                                    bottom: 340,
                                    right: 510,
                                    zIndex: 1500,
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        width: 360,
                                        p: '32px',
                                        backgroundImage: `url(${backgroundImageFile})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        bgcolor: '#FFFFFF',
                                        borderRadius: '20px',
                                        textAlign: 'center',
                                        border: '1px solid #EAEAE6'
                                    }}
                                >
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => setShowAiNotification(false)}
                                        sx={{
                                            position: 'absolute', top: 16, right: 16, color: '#374151',
                                            bgcolor: '#F3F4F6', '&:hover': { bgcolor: '#E5E7EB' }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: '1.25rem' }} />
                                    </IconButton>

                                    <Typography
                                        variant="h4" component="h2"
                                        sx={{
                                            fontWeight: 'bold', color: '#75a84b', mb: 2, lineHeight: 1.25,
                                            fontSize: '1.75rem'
                                        }}
                                    >
                                        Submit a quick <br /> order with AI
                                    </Typography>
                                    <Button
                                        variant="contained" fullWidth
                                        onClick={() => {
                                            onAskAiClick();
                                            setShowAiNotification(false);
                                        }}
                                        sx={{
                                            bgcolor: '#75a84b', color: 'white', borderRadius: '10px', py: 1.25,
                                            fontWeight: 'bold', fontSize: '1rem', textTransform: 'none',
                                            boxShadow: 'none', '&:hover': { bgcolor: '#16A34A', boxShadow: 'none' }
                                        }}
                                    >
                                        Quick Order Now
                                    </Button>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#374151', mt: 2, fontSize: '0.875rem' }}
                                    >
                                        Just click above, it's fast, easy and smart.
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Box>

                    </>
                )}
            </AnimatePresence>
            {/* --- END OF EDIT --- */}

            {/* Special Request Dialog */}
            <Dialog
                open={isRequestOpen}
                onClose={() => setIsRequestOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 'bold',
                    color: '#75a84b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <AddCircleOutlineIcon /> Add Special Item
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3, color: '#555' }}>
                        Can't find what you're looking for in the catalog? Add it here!
                        It will be added directly to your cart.
                    </DialogContentText>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                label="Item Name & Description"
                                placeholder="E.g. Large Watermelon, Brand X 500g..."
                                fullWidth
                                variant="outlined"
                                multiline
                                rows={2}
                                value={specialItemName}
                                onChange={(e) => setSpecialItemName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Quantity"
                                type="number"
                                fullWidth
                                value={specialItemQty}
                                onChange={(e) => setSpecialItemQty(e.target.value)}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Unit / Mode</InputLabel>
                                <Select
                                    value={specialItemUnit}
                                    label="Unit / Mode"
                                    onChange={(e) => setSpecialItemUnit(e.target.value)}
                                >
                                    <MenuItem value="piece">Piece</MenuItem>
                                    <MenuItem value="case">Case</MenuItem>
                                    <MenuItem value="bag">Bag</MenuItem>
                                    <MenuItem value="lb">Lb (Weight)</MenuItem>
                                    <MenuItem value="box">Box</MenuItem>
                                    <MenuItem value="tray">Tray</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                * Price for special items will be determined and updated on the final invoice.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Matax will do its best to fulfill this special request. If we're unable to, we'll notify you once we finish processing your order.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: '0 24px 20px' }}>
                    <Button onClick={() => setIsRequestOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
                    <Button
                        onClick={handleSpecialRequestSubmit}
                        disabled={requestLoading}
                        variant="contained"
                        startIcon={requestLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                        sx={{
                            backgroundColor: '#75a84b',
                            px: 4,
                            py: 1,
                            '&:hover': { backgroundColor: '#7a8a54' }
                        }}
                    >
                        {requestLoading ? 'Adding...' : 'Add to Cart'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Header;
